/**
 * Nav lights, strobes, work lights, sensor eyes and beacons at LIGHT_ sockets (style guide §3.6, §3.7, §9.3;
 * D-047 lighting overhaul). Asset-lane owned (task A11). One camera-facing sprite per light: a tight E1 core plus a
 * soft halo, kept above a minimum on-screen size (halo ≥ 6 px at 1080p) so the light signature reads to kilometres
 * and on Low without bloom. Strobes and beacons flash on the library clock (80 ms per period, dim between flashes);
 * nav lights burn steady.
 */
import {
  AdditiveBlending,
  Box3,
  Color,
  ColorManagement,
  type Frustum,
  type FrustumArray,
  Group,
  InstancedBufferAttribute,
  Matrix4,
  Sprite,
  Sphere,
  SpriteNodeMaterial,
  Vector3,
  type Node,
  type Object3D,
} from 'three/webgpu'
import {
  cameraProjectionMatrix,
  color,
  float,
  fract,
  instancedBufferAttribute,
  max,
  modelViewMatrix,
  modelViewPosition,
  modelWorldMatrix,
  pow,
  saturate,
  select,
  uniform,
  uv,
  varying,
  vec4,
} from 'three/tsl'
import type { ModelInstance } from '../../assets/loader'
import { logger } from '../../core/logger'
import { flaggedUsage } from '../uploads'
import { asV3, unitColorY } from './hull/surface'
import { internals, type MaterialLibrary } from './library'

export interface ShipLights {
  dispose(): void
}

/** Light sprite tuning (style guide §3.7). */
export const LIGHT_FX = {
  /** World diameter (m) of the sprite for steady lights and for strobes/beacons. */
  size: 0.55,
  strobeSize: 0.9,
  /** Minimum halo diameter in pixels at 1080p. */
  haloPx: 6,
  /** Flash length (s) of strobes and beacons, and the dim level between flashes. */
  flash: 0.08,
  dim: 0.04,
} as const

/** A light's parameters: a `LIGHT_` socket's userData (asset contract) or a stand-alone light's. */
export interface LightParams {
  color?: unknown
  blinkHz?: unknown
  phase?: unknown
  type?: unknown
}

/** One light's look (style guide §3.7), shared by `lightSprite` and the pool so both draw the same maths. */
export interface LightLook {
  /** sRGB hex of the light colour. */
  readonly hex: string
  readonly hz: number
  readonly phase: number
  /** Strobes, beacons and any light with a blink rate. */
  readonly flashing: boolean
  /** Share of each period at full; 1 = steady (no blink). Between flashes the light sits at `LIGHT_FX.dim`. */
  readonly duty: number
  /** Luminance (Y) of the light at full. */
  readonly peakY: number
  /** World diameter (m). */
  readonly size: number
}

export function lightLook(lib: MaterialLibrary, params: LightParams): LightLook {
  const hex = typeof params.color === 'string' ? params.color : '#FFFFFF'
  const hz = typeof params.blinkHz === 'number' ? params.blinkHz : 0
  const phase = typeof params.phase === 'number' ? params.phase : 0
  const data = internals(lib).data
  const flashing = params.type === 'strobe' || params.type === 'beacon' || hz > 0
  const duty = flashing && hz > 0 ? Math.min(LIGHT_FX.flash * Math.max(hz, 0.1), 0.5) : 1
  const peakY = params.type === 'strobe' ? data.emissive.strobePeakY : data.emissive.signalY
  return { hex, hz, phase, flashing, duty, peakY, size: flashing ? LIGHT_FX.strobeSize : LIGHT_FX.size }
}

function lightSprite(lib: MaterialLibrary, node: Object3D): Sprite {
  const look = lightLook(lib, node.userData as LightParams)
  const li = internals(lib)
  const m = new SpriteNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  m.name = `light:${node.name}`
  // Minimum on-screen size: grow the sprite once its world size shrinks under `haloPx` pixels (1080p reference).
  const worldSize = modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz.length()
  const p11 = cameraProjectionMatrix.mul(vec4(0, 1, 0, 0)).y
  const pxToWorld = modelViewPosition.z.negate().mul(2).div(p11.mul(1080))
  m.scaleNode = max(float(1), pxToWorld.mul(LIGHT_FX.haloPx).div(max(worldSize, 1e-4)))
  const on = look.duty < 1 ? select(fract(li.time.mul(look.hz).add(look.phase)).lessThan(look.duty), float(1), float(LIGHT_FX.dim)) : float(1)
  m.colorNode = unitColorY(asV3(color(look.hex)), uniform(look.peakY)).mul(lightShape()).mul(on)
  m.opacityNode = float(1)
  const s = new Sprite(m)
  s.name = `light:${node.name}`
  s.renderOrder = 12
  s.scale.setScalar(look.size)
  return s
}

/** The radial core plus halo of every light (1 at the centre). */
function lightShape(): Node<'float'> {
  const d = uv().sub(0.5).length().mul(2)
  const core = pow(saturate(float(1).sub(d.mul(4))), 2)
  const halo = pow(saturate(float(1).sub(d)), 2.5).mul(0.3)
  return core.add(halo)
}

export function attachShipLights(lib: MaterialLibrary, instance: ModelInstance): ShipLights {
  const sprites: Sprite[] = []
  for (const [name, node] of instance.sockets) {
    if (!name.startsWith('LIGHT_')) continue
    const s = lightSprite(lib, node)
    node.add(s)
    sprites.push(s)
  }
  return {
    dispose(): void {
      for (const s of sprites) {
        s.removeFromParent()
        s.material.dispose()
      }
    },
  }
}

/** M7 S43 (additive): a stand-alone light sprite (a restarted derelict's windows, beacon and docking light). */
export interface LightSpriteParams {
  color: string
  /** Flashes on the library clock at this rate (a beacon); 0 or absent = steady. */
  blinkHz?: number
}

export function createLightSprite(lib: MaterialLibrary, name: string, params: LightSpriteParams): Sprite {
  const node = new Group()
  node.name = name
  node.userData = { color: params.color, blinkHz: params.blinkHz ?? 0, type: (params.blinkHz ?? 0) > 0 ? 'beacon' : 'nav' }
  return lightSprite(lib, node)
}

/**
 * PERF PF-5a (D-TBD PERF-7): every ship hull light in one instanced draw. One `Sprite` drawn `count` times (three's
 * instanced sprite path: the same billboard as `lightSprite`), with per-instance render-space position, colour
 * (already scaled to its peak Y) and `aLight` = (blink Hz, phase, duty, world size). Size 0 hides an instance.
 *
 * Positions come from each root's **current** pose (`root.updateMatrix()` × the socket's offset in root space, cached
 * at `add`), never from `matrixWorld`, which three refreshes only inside `renderer.render`: read after the views
 * move, it is last frame's, and in camera-relative render space the lights would trail their hulls (review M4).
 * So the pool's `object` must share the roots' parent and keep an identity transform; a light whose root has left
 * that parent, or whose socket chain is hidden, draws at size 0. Ship hulls only: stations, mount modules and the
 * derelict keep their sprites (`attachShipLights`, `createLightSprite`). PF-5b wires the ship views to it.
 */
export interface LightPool {
  /** The one draw. Add it to the ship roots' parent (render space) and leave its transform at identity. */
  readonly object: Sprite
  readonly capacity: number
  /** Live lights; instances 0..count-1 are dense. */
  readonly count: number
  /** The instance data (read-only for callers): render-space xyz, linear rgb at peak Y, and (Hz, phase, duty, size). */
  readonly positions: Float32Array
  readonly colors: Float32Array
  readonly lights: Float32Array
  /**
   * Adds a light at `socket` (a descendant of `root`, or `root` itself, rigid on the hull: its offset is cached
   * here). `params` defaults to the socket's userData. Returns a stable slot handle, or -1 when the pool is full.
   */
  add(root: Object3D, socket: Object3D, params?: LightParams): number
  /** Frees a slot (swap-remove: the last instance moves into its place). Unknown or freed slots are ignored. */
  remove(slot: number): void
  /** Writes every live light's render-space position and size from its root's current pose. Allocation-free. */
  update(): void
  dispose(): void
}

const POOL_NAME = 'light:pool'
const scratchM = new Matrix4()
const scratchC = new Color()
const lumK = new Vector3()

/** True when every node from `socket` up to `root` is visible. */
function isShown(socket: Object3D, root: Object3D): boolean {
  for (let o: Object3D | null = socket; o !== null; o = o.parent) {
    if (!o.visible) return false
    if (o === root) return true
  }
  return false
}

/** The pool draw's material (shared by the ship pool and the static pool): `lightSprite`'s maths per instance. */
function poolMaterial(
  lib: MaterialLibrary,
  aPos: InstancedBufferAttribute,
  aCol: InstancedBufferAttribute,
  aLight: InstancedBufferAttribute,
  name: string,
): SpriteNodeMaterial {
  const li = internals(lib)
  const pos = instancedBufferAttribute<'vec3'>(aPos, 'vec3')
  const col = instancedBufferAttribute<'vec3'>(aCol, 'vec3')
  const light = instancedBufferAttribute<'vec4'>(aLight, 'vec4')
  const m = new SpriteNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  m.name = name
  m.positionNode = pos
  // `lightSprite`'s minimum on-screen size per instance. three scales the quad by the object's world scale S, so the
  // light's world diameter is size × S (S = 1 for the ship pool at identity in render space; the station root's
  // scale for a static pool) and the floor grows it to `haloPx` pixels: max(size × S, px) as `lightSprite` does.
  const size = light.w
  const worldSize = size.mul(modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz.length())
  const p11 = cameraProjectionMatrix.mul(vec4(0, 1, 0, 0)).y
  const pxToWorld = modelViewMatrix.mul(vec4(pos, 1)).z.negate().mul(2).div(p11.mul(1080))
  m.scaleNode = size.mul(max(float(1), pxToWorld.mul(LIGHT_FX.haloPx).div(max(worldSize, 1e-4))))
  // `lightSprite`'s blink: full for `duty` of each period, `dim` between (duty 1 = steady).
  const on = select(fract(li.time.mul(light.x).add(light.y)).lessThan(light.z), float(1), float(LIGHT_FX.dim))
  m.colorNode = varying(col.mul(on), 'vLightColor').mul(lightShape())
  m.opacityNode = float(1)
  return m
}

/**
 * Writes light `i`'s colour (`unitColorY` on the CPU: the linear colour scaled so its luminance is the peak Y) and
 * (Hz, phase, duty) into the pool arrays; the caller writes the size (`.w`). Returns the light's look.
 */
function writeLight(lib: MaterialLibrary, params: LightParams, i: number, colArr: Float32Array, lightArr: Float32Array): LightLook {
  const look = lightLook(lib, params)
  scratchC.set(look.hex)
  ColorManagement.getLuminanceCoefficients(lumK)
  const k = look.peakY / Math.max(scratchC.r * lumK.x + scratchC.g * lumK.y + scratchC.b * lumK.z, 1e-3)
  colArr[i * 3] = scratchC.r * k
  colArr[i * 3 + 1] = scratchC.g * k
  colArr[i * 3 + 2] = scratchC.b * k
  lightArr[i * 4] = look.hz
  lightArr[i * 4 + 1] = look.phase
  lightArr[i * 4 + 2] = look.duty
  return look
}

export function createLightPool(lib: MaterialLibrary, capacity: number): LightPool {
  const cap = Math.max(1, Math.floor(capacity))
  const posArr = new Float32Array(cap * 3)
  const colArr = new Float32Array(cap * 3)
  const lightArr = new Float32Array(cap * 4)
  const aPos = new InstancedBufferAttribute(posArr, 3).setUsage(flaggedUsage())
  const aCol = new InstancedBufferAttribute(colArr, 3).setUsage(flaggedUsage())
  const aLight = new InstancedBufferAttribute(lightArr, 4).setUsage(flaggedUsage())
  /** Per dense index: the socket origin in root space, and the light's size × the socket-to-root scale. */
  const offset = new Float64Array(cap * 3)
  const baseSize = new Float64Array(cap)
  const roots = new Array<Object3D | null>(cap).fill(null)
  const sockets = new Array<Object3D | null>(cap).fill(null)
  /** Dense index → slot handle, slot handle → dense index (-1 = free), and the free-handle stack. */
  const handleOf = new Int32Array(cap)
  const indexOf = new Int32Array(cap).fill(-1)
  const free = new Int32Array(cap)
  for (let i = 0; i < cap; i++) free[i] = cap - 1 - i
  let freeTop = cap
  let n = 0
  let warned = false

  const m = poolMaterial(lib, aPos, aCol, aLight, POOL_NAME)
  const object = new Sprite(m)
  object.name = POOL_NAME
  object.renderOrder = 12
  object.frustumCulled = false
  object.count = 0
  object.visible = false

  function move(from: number, to: number): void {
    for (let k = 0; k < 3; k++) {
      posArr[to * 3 + k] = posArr[from * 3 + k]
      colArr[to * 3 + k] = colArr[from * 3 + k]
      offset[to * 3 + k] = offset[from * 3 + k]
    }
    for (let k = 0; k < 4; k++) lightArr[to * 4 + k] = lightArr[from * 4 + k]
    baseSize[to] = baseSize[from]
    roots[to] = roots[from]
    sockets[to] = sockets[from]
    handleOf[to] = handleOf[from]
    indexOf[handleOf[to]] = to
  }

  function setCount(): void {
    object.count = n
    object.visible = n > 0
  }

  return {
    object,
    capacity: cap,
    positions: posArr,
    colors: colArr,
    lights: lightArr,
    get count(): number {
      return n
    },
    add(root: Object3D, socket: Object3D, params?: LightParams): number {
      if (freeTop === 0) {
        if (!warned) logger.warn('light pool full; light dropped', { capacity: cap })
        warned = true
        return -1
      }
      scratchM.identity()
      for (let o: Object3D | null = socket; o !== root; o = o.parent) {
        if (o === null) throw new Error(`light socket ${socket.name} is not under root ${root.name}`)
        o.updateMatrix()
        scratchM.premultiply(o.matrix)
      }
      const e = scratchM.elements
      const look = writeLight(lib, params ?? (socket.userData as LightParams), n, colArr, lightArr)
      const slot = free[--freeTop]
      const i = n++
      handleOf[i] = slot
      indexOf[slot] = i
      roots[i] = root
      sockets[i] = socket
      offset[i * 3] = e[12]
      offset[i * 3 + 1] = e[13]
      offset[i * 3 + 2] = e[14]
      baseSize[i] = look.size * Math.hypot(e[0], e[1], e[2])
      lightArr[i * 4 + 3] = 0
      aCol.needsUpdate = true
      aLight.needsUpdate = true
      setCount()
      return slot
    },
    remove(slot: number): void {
      if (!Number.isInteger(slot) || slot < 0 || slot >= cap) return
      const i = indexOf[slot]
      if (i < 0) return
      const last = n - 1
      if (i !== last) move(last, i)
      roots[last] = null
      sockets[last] = null
      indexOf[slot] = -1
      free[freeTop++] = slot
      n = last
      aPos.needsUpdate = true
      aCol.needsUpdate = true
      aLight.needsUpdate = true
      setCount()
    },
    update(): void {
      const parent = object.parent
      for (let i = 0; i < n; i++) {
        const root = roots[i] as Object3D
        const socket = sockets[i] as Object3D
        root.updateMatrix()
        const e = root.matrix.elements
        const ox = offset[i * 3]
        const oy = offset[i * 3 + 1]
        const oz = offset[i * 3 + 2]
        posArr[i * 3] = e[0] * ox + e[4] * oy + e[8] * oz + e[12]
        posArr[i * 3 + 1] = e[1] * ox + e[5] * oy + e[9] * oz + e[13]
        posArr[i * 3 + 2] = e[2] * ox + e[6] * oy + e[10] * oz + e[14]
        const shown = root.parent === parent && isShown(socket, root)
        lightArr[i * 4 + 3] = shown ? baseSize[i] * Math.sqrt(e[0] * e[0] + e[1] * e[1] + e[2] * e[2]) : 0
      }
      if (n > 0) {
        aPos.needsUpdate = true
        aLight.needsUpdate = true
      }
    },
    dispose(): void {
      object.removeFromParent()
      m.dispose()
      for (let i = 0; i < n; i++) {
        roots[i] = null
        sockets[i] = null
      }
      n = 0
      setCount()
    },
  }
}

/**
 * Static-model light pool (D-TBD, scale W4 light-pool builder): every `LIGHT_` socket of one static model instance
 * (station, structure, prop, mount module) in **one** instanced draw instead of one `lightSprite` each. The same
 * per-light maths as the ship pool (`poolMaterial`, `writeLight`: colour at its peak Y, blink Hz, phase, duty, world
 * size and the `haloPx` floor); positions are the sockets' offsets in the model root's space, and the draw is a child
 * of the root at identity, so three's world matrix carries it: **no per-frame CPU work**. Static means static: the
 * sockets (direct children of the root, asset contract) are read once here; a later per-socket visibility or pose
 * change is not followed (nothing in the game makes one on a static model; wrecks and the derelict keep sprites).
 * Culled as one by a sphere around its lights (each light's centre plus its sprite's own cull radius, as three culls
 * a lone sprite). `setStaticLightsVisible` (station light scale) shows or hides the whole set.
 */
export interface StaticLightPool extends ShipLights {
  /** The one draw: a child of the model root. */
  readonly object: Sprite
  readonly count: number
  /** Instance data (read-only): root-space xyz, linear rgb at peak Y, (Hz, phase, duty, world size in root space). */
  readonly positions: Float32Array
  readonly colors: Float32Array
  readonly lights: Float32Array
  /** The cull sphere in root space. */
  readonly bounds: Sphere
}

const STATIC_POOL_NAME = 'light:static-pool'
/** three's cull radius of a unit sprite (`Frustum.intersectsSprite`, centre (0.5, 0.5)). */
const SPRITE_CULL_RADIUS = Math.SQRT1_2
const cullSphere = new Sphere()
const staticPools = new WeakMap<Object3D, StaticLightPool>()

/** The static light pool of `root`'s instance, if it has one. */
export function staticLightPoolOf(root: Object3D): StaticLightPool | undefined {
  return staticPools.get(root)
}

/** Shows or hides an instance's lights: the sprites under its `LIGHT_` sockets and its static pool. Not per frame. */
export function setStaticLightsVisible(instance: Pick<ModelInstance, 'sockets'> & { readonly root?: Object3D }, on: boolean): void {
  for (const [name, node] of instance.sockets) {
    if (!name.startsWith('LIGHT_')) continue
    for (const child of node.children) child.visible = on
  }
  const pool = instance.root === undefined ? undefined : staticPools.get(instance.root)
  if (pool) pool.object.visible = on
}

/** Pools `instance`'s `LIGHT_` sockets (null when it has none). At spawn only, never per frame. */
export function createStaticLightPool(lib: MaterialLibrary, instance: ModelInstance): StaticLightPool | null {
  const sockets: Object3D[] = []
  for (const [name, node] of instance.sockets) if (name.startsWith('LIGHT_')) sockets.push(node)
  const n = sockets.length
  if (n === 0) return null
  const posArr = new Float32Array(n * 3)
  const colArr = new Float32Array(n * 3)
  const lightArr = new Float32Array(n * 4)
  const bounds = new Sphere()
  const box = new Box3()
  const p = new Vector3()
  let maxCull = 0
  for (let i = 0; i < n; i++) {
    const socket = sockets[i]
    // Sockets are the root's direct children (asset contract); walk up anyway so a nested one still lands right.
    scratchM.identity()
    for (let o: Object3D | null = socket; o !== instance.root; o = o.parent) {
      if (o === null) throw new Error(`light socket ${socket.name} is not under root ${instance.root.name}`)
      o.updateMatrix()
      scratchM.premultiply(o.matrix)
    }
    const e = scratchM.elements
    posArr[i * 3] = e[12]
    posArr[i * 3 + 1] = e[13]
    posArr[i * 3 + 2] = e[14]
    const look = writeLight(lib, socket.userData as LightParams, i, colArr, lightArr)
    // `lightSprite`'s world size under the root: its scale × the socket chain's scale.
    const size = look.size * Math.hypot(e[0], e[1], e[2])
    lightArr[i * 4 + 3] = size
    box.expandByPoint(p.set(e[12], e[13], e[14]))
    maxCull = Math.max(maxCull, size * SPRITE_CULL_RADIUS)
  }
  box.getCenter(bounds.center)
  let r = 0
  for (let i = 0; i < n; i++) r = Math.max(r, bounds.center.distanceTo(p.fromArray(posArr, i * 3)))
  bounds.radius = r + maxCull

  const m = poolMaterial(
    lib,
    new InstancedBufferAttribute(posArr, 3),
    new InstancedBufferAttribute(colArr, 3),
    new InstancedBufferAttribute(lightArr, 4),
    STATIC_POOL_NAME,
  )
  const object = new Sprite(m)
  object.name = STATIC_POOL_NAME
  object.renderOrder = 12
  object.count = n
  object.frustumCulled = true
  // One sphere around every light, in the root's space (the pool sits at identity under it). Allocation-free.
  object.intersectsFrustum = function intersectsFrustum(frustum: Frustum | FrustumArray): boolean {
    cullSphere.copy(bounds).applyMatrix4(this.matrixWorld)
    return frustum.intersectsSphere(cullSphere)
  }
  instance.root.add(object)
  const pool: StaticLightPool = {
    object,
    count: n,
    positions: posArr,
    colors: colArr,
    lights: lightArr,
    bounds,
    dispose(): void {
      object.removeFromParent()
      m.dispose()
      staticPools.delete(instance.root)
    },
  }
  staticPools.set(instance.root, pool)
  return pool
}
